import ArrowDownIcon from "@/assets/images/icons/arrow-down.svg";
import ArrowRightIcon from "@/assets/images/icons/arrow-right.svg";
import CoursesBookmarkIcon from "@/assets/images/icons/courses_bookmark_icon.svg";
import FolderIcon from "@/assets/images/icons/folder.svg";
import PlayCircleIcon from "@/assets/images/icons/play-circle.svg";
import ExternalLink from "@/components/ExternalLink";
import { Popover } from "@/components/ui/Popover";
import { Sheet, SheetDragHandle, SheetScrollView } from "@/components/ui/Sheet";
import { BodyText, CaptionText, CardTitle } from "@/components/ui/Typography";
import { useAuth } from "@/lib/AuthContext";
import { URLS } from "@/lib/constants";
import { logger } from "@/lib/logger";
import { useIsMobile } from "@/lib/useDeviceType";
import {
  fetchAllFolderBookmarks,
  fetchUserFolders,
  UserBookmark,
  UserFolder,
} from "@/supabase/services/userFolders";
import { Ionicons } from "@expo/vector-icons";
import { type Href, useRouter } from "expo-router";
import React, { useCallback, useState } from "react";
import { Platform, ScrollView, View } from "react-native";
import { Pressable } from "react-native-gesture-handler";
import { Accordion, Button, XStack, YStack } from "tamagui";

const PANEL_SHELL_SHADOW =
  Platform.OS === "web"
    ? ({
        boxShadow:
          "0 4px 8px 0 rgba(0, 0, 0, 0.10), 0 0 4px 0 rgba(0, 0, 0, 0.08)",
      } as const)
    : {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 6,
      };

/** Match ExternalLink defer — let popover/sheet finish closing before push */
const NAV_DEFER_MS = 150;

interface BookmarkManagerProps {
  children?: React.ReactNode; // Trigger element
  /** Controlled open state (used when triggered externally, e.g. bottom bar). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Render only the sheet, no trigger (controlled from outside). */
  hideTrigger?: boolean;
}

const PUBLISHED_STATUS = 3;

export function BookmarkManager({
  children,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: BookmarkManagerProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [sheetOpenState, setSheetOpenState] = useState(false);
  const [folders, setFolders] = useState<UserFolder[]>([]);
  const [folderBookmarks, setFolderBookmarks] = useState<
    Record<number, UserBookmark[]>
  >({});
  const [expandedFolder, setExpandedFolder] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);

  const { user } = useAuth();
  const isMobile = useIsMobile();
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  // When controlled (bottom bar), open state comes from props.
  const sheetOpen = controlledOpen ?? sheetOpenState;
  const setSheetOpen = useCallback(
    (open: boolean) => {
      if (controlledOpen === undefined) setSheetOpenState(open);
      onOpenChange?.(open);
    },
    [controlledOpen, onOpenChange],
  );

  const closePanels = useCallback(() => {
    setModalOpen(false);
    setSheetOpen(false);
  }, [setSheetOpen]);

  const isCourseBookmark = useCallback((bookmark: UserBookmark): boolean => {
    return (
      !!bookmark.course_id &&
      !bookmark.lesson_id &&
      bookmark.old_lesson_id == null
    );
  }, []);

  const isInternalBookmark = useCallback(
    (bookmark: UserBookmark): boolean => {
      if (
        bookmark.lesson_id != null &&
        bookmark.lesson_status === PUBLISHED_STATUS
      )
        return true;
      if (
        isCourseBookmark(bookmark) &&
        bookmark.course_status === PUBLISHED_STATUS
      )
        return true;
      return false;
    },
    [isCourseBookmark],
  );

  const loadUserFolders = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setLoadError(null);
      const userFolders = await fetchUserFolders(user.id);
      setFolders(userFolders);

      const grouped = await fetchAllFolderBookmarks(user.id);
      setFolderBookmarks(grouped);
      if (userFolders.length > 0) {
        setExpandedFolder(userFolders[0].id.toString());
      }
    } catch (error: any) {
      logger.error("Error loading user folders:", error);
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const getBookmarkUrl = useCallback(
    (bookmark: UserBookmark): string => {
      if (!bookmark.url) return "#";
      if (isInternalBookmark(bookmark)) {
        return bookmark.url;
      }
      const baseUrl = URLS.WORDPRESS_BASE;
      const url = bookmark.url.startsWith("http")
        ? bookmark.url
        : `${baseUrl}${bookmark.url.startsWith("/") ? "" : "/"}${bookmark.url}`;
      return url.endsWith("/") ? url : `${url}/`;
    },
    [isInternalBookmark],
  );

  const handleBookmarkPress = useCallback(() => {
    // Close first; ExternalLink deferNavigation waits before push
    closePanels();
  }, [closePanels]);

  const goToManagePage = useCallback(() => {
    closePanels();
    setTimeout(() => {
      // Switch to the bookmarks tab stack (not push onto home)
      router.navigate("/bookmarks" as Href);
    }, NAV_DEFER_MS);
  }, [closePanels, router]);

  const handleMobileOpen = () => {
    void loadUserFolders();
    setSheetOpen(true);
  };

  // Load when sheet opens (header trigger) or on mount (bottom bar — mounted only while open).
  React.useEffect(() => {
    if (sheetOpen || hideTrigger) {
      void loadUserFolders();
    }
  }, [sheetOpen, hideTrigger, loadUserFolders]);

  const handleSheetOpenChange = useCallback(
    (open: boolean) => {
      if (open === sheetOpen) return;
      setSheetOpen(open);
      if (open) setModalOpen(false);
    },
    [sheetOpen, setSheetOpen],
  );

  const renderContent = (inSheet: boolean) => {
    const ScrollComponent: any = inSheet ? SheetScrollView : ScrollView;
    return (
      <YStack
        w="100%"
        f={inSheet ? 1 : undefined}
        backgroundColor="white"
        borderRadius={inSheet ? 0 : "$4"}
        overflow="hidden"
        style={inSheet ? undefined : PANEL_SHELL_SHADOW}
      >
        {/* Header */}
        <XStack
          ai="center"
          jc="space-between"
          p="$3"
          borderBottomWidth={1}
          borderBottomColor="$borderColor"
        >
          <CardTitle fontWeight="600" color="#0D0D0D">
            My Bookmarks
          </CardTitle>
          <Pressable
            onPress={goToManagePage}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel="Manage bookmarks"
          >
            <Ionicons name="options-outline" size={22} color="#5C5854" />
          </Pressable>
        </XStack>

        <ScrollComponent
          style={
            inSheet
              ? { flex: 1, minHeight: 0, backgroundColor: "white" }
              : { maxHeight: 340 }
          }
          contentContainerStyle={inSheet ? { flexGrow: 1 } : undefined}
          showsVerticalScrollIndicator={false}
          contentInsetAdjustmentBehavior="never"
        >
          <YStack p="$3" gap="$2">
            {loading ? (
              <YStack padding={24} ai="center">
                <BodyText color="#666">Loading...</BodyText>
              </YStack>
            ) : loadError ? (
              <YStack ai="center" p="$4" gap="$3">
                <BodyText color="#666" textAlign="center">
                  {loadError?.message || "Failed to load folders"}
                </BodyText>
                <Button
                  size="$3"
                  bg="#374151"
                  color="white"
                  br="$6"
                  onPress={() => void loadUserFolders()}
                  pressStyle={{ opacity: 0.8 }}
                >
                  Try again
                </Button>
              </YStack>
            ) : folders.length === 0 ? (
              <BodyText color="#666" textAlign="center" p="$4">
                No folders yet. Create your first folder from a lesson!
              </BodyText>
            ) : (
              folders.map((folder) => (
                <Accordion
                  key={folder.id}
                  type="single"
                  value={
                    expandedFolder === folder.id.toString()
                      ? folder.id.toString()
                      : ""
                  }
                >
                  <Accordion.Item value={folder.id.toString()}>
                    <Pressable
                      onPress={() => {
                        if (expandedFolder === folder.id.toString()) {
                          setExpandedFolder(null);
                        } else {
                          setExpandedFolder(folder.id.toString());
                        }
                      }}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        paddingVertical: 12,
                        paddingHorizontal: 10,
                        borderRadius: 14,
                        backgroundColor:
                          expandedFolder === folder.id.toString()
                            ? "#F5F3EE"
                            : "transparent",
                      }}
                    >
                      {expandedFolder === folder.id.toString() ? (
                        <ArrowDownIcon
                          width={12}
                          height={12}
                          color="#111"
                          style={{ marginRight: 10 }}
                        />
                      ) : (
                        <ArrowRightIcon
                          width={12}
                          height={12}
                          color="#111"
                          style={{ marginRight: 10 }}
                        />
                      )}
                      <FolderIcon
                        width={16}
                        height={16}
                        color="#666666"
                        style={{ marginRight: 10 }}
                      />
                      <CardTitle
                        fontWeight="600"
                        color="#0D0D0D"
                        flex={1}
                        textAlign="left"
                      >
                        {folder.title}
                      </CardTitle>
                    </Pressable>

                    {expandedFolder === folder.id.toString() && (
                      <YStack
                        gap="$2"
                        paddingLeft="$6"
                        paddingTop="$2"
                        paddingBottom="$2"
                      >
                        {(folderBookmarks[folder.id]?.length ?? 0) === 0 ? (
                          <CaptionText color="#666" padding="$2">
                            No lessons in this folder yet
                          </CaptionText>
                        ) : (
                          folderBookmarks[folder.id]!.map((bm) => {
                            const bookmarkUrl = getBookmarkUrl(bm);
                            const isCourseBookmarkValue = isCourseBookmark(bm);
                            const isInternalBookmarkValue =
                              isInternalBookmark(bm);
                            const displayTitle = isInternalBookmarkValue
                              ? isCourseBookmarkValue
                                ? (bm.course_title ?? "Course")
                                : (bm.lesson_title ?? "Lesson")
                              : bm.title?.trim() ||
                                (isCourseBookmarkValue ? "Course" : "Lesson");

                            return (
                              <XStack
                                key={bm.id}
                                ai="center"
                                flex={1}
                                style={{
                                  padding: 8,
                                  paddingLeft: 20,
                                  borderRadius: 6,
                                }}
                              >
                                <XStack
                                  width={16}
                                  height={16}
                                  mr="$2.5"
                                  ai="center"
                                  jc="center"
                                  pointerEvents="none"
                                >
                                  {isCourseBookmarkValue ? (
                                    <CoursesBookmarkIcon
                                      width={16}
                                      height={16}
                                    />
                                  ) : (
                                    <PlayCircleIcon
                                      width={16}
                                      height={16}
                                      color="#111"
                                    />
                                  )}
                                </XStack>
                                <ExternalLink
                                  url={bookmarkUrl}
                                  onPress={handleBookmarkPress}
                                  deferNavigation
                                >
                                  <CardTitle
                                    fontWeight="600"
                                    color="#0D0D0D"
                                    numberOfLines={2}
                                    flexShrink={1}
                                  >
                                    {displayTitle}
                                  </CardTitle>
                                </ExternalLink>
                              </XStack>
                            );
                          })
                        )}
                      </YStack>
                    )}
                  </Accordion.Item>
                </Accordion>
              ))
            )}
          </YStack>
        </ScrollComponent>
      </YStack>
    );
  };

  const triggerWrap = (
    <View style={{ paddingVertical: 2, paddingHorizontal: 2, borderRadius: 6 }}>
      {children}
    </View>
  );

  const sheetNode = (
    <Sheet
      open={(hideTrigger || isMobile) && sheetOpen}
      onOpenChange={handleSheetOpenChange}
      snapPoints={[65, 50]}
      backgroundColor="white"
    >
      <YStack f={1} backgroundColor="white">
        <SheetDragHandle />
        <YStack f={1}>{renderContent(true)}</YStack>
      </YStack>
    </Sheet>
  );

  if (hideTrigger) {
    return sheetNode;
  }

  return (
    <>
      <Popover
        open={!isMobile && modalOpen}
        onOpenChange={(open) => {
          if (open === modalOpen) return;
          setModalOpen(open);
          if (open) {
            void loadUserFolders();
            setSheetOpen(false);
          }
        }}
        onMobileOpen={handleMobileOpen}
        placement="bottom-end"
        offset={30}
        width={360}
        maxWidth={360}
        maxHeight={420}
        contentBackgroundColor="transparent"
        bareContent
        content={renderContent(false)}
      >
        {triggerWrap}
      </Popover>

      {sheetNode}
    </>
  );
}
